import React from "react";
import * as z from "zod";
import type {FieldError as HookFormFieldError} from "react-hook-form";

import RequirementsList from "@/main/react/component/RequirementsList";

export type ValidationRule = {
    id: string;
    text: string;
    /**
     * A message chosen from the VALUE, when one rule legitimately answers two different states.
     *
     * `CREATE-PUBLISH-36`, 2026-08-26. The publish gate had one body rule and one message — "Write at
     * least 10 characters in your post." — so a composer with nothing in it and a composer with seven
     * characters in it got the same sentence, and the register's own funnel figure (190 blank-page
     * abandonments to 26 blocked) says the blank case is the dominant population arriving there. It
     * was answering the commonest state with copy written for the rarer one.
     *
     * <p>A second RULE would have been the defect restated: both items fail on an empty draft, so the
     * character floor would print beside the empty message. One rule, one item, one line — with the
     * line chosen from what the member actually has.
     *
     * <p>Optional, and `text` stays required: a rule that has nothing state-dependent to say defines
     * only `text`, and every existing rule is bit-for-bit unchanged.
     */
    textFor?: (value: string) => string;
    validate: (value: string) => boolean;
};

export type ValidationRulesMap = Record<string, ValidationRule[]>;
export type ValidationErrorsMap = Record<string, string[]>;

export type RequirementItem = {
    id: string;
    text: string;
    met: boolean;
};

export type RequirementFieldState = {
    items: RequirementItem[];
    visible: boolean;
    highlight: boolean;
    failedMessages: string[];
};

type UseRequirementsOptions = {
    autoShowOnValidate?: boolean;
    hideWhenSatisfied?: boolean;
    showOnlyFailedOnValidateAll?: boolean;
};

type CollectValidationErrorsOptions = {
    stopAtFirstErrorPerField?: boolean;
};

/**
 * The message a rule shows for THIS value — its `textFor` when it has one, its static `text` otherwise.
 *
 * A throwing `textFor` falls back to the static message rather than taking the panel down with it:
 * this runs inside the publish gate, and a broken message is a far smaller failure than a composer
 * that cannot tell the author why it will not publish.
 */
function messageFor(rule: ValidationRule, value: string): string {
    if (!rule.textFor) {
        return rule.text;
    }
    try {
        return rule.textFor(value) || rule.text;
    } catch {
        return rule.text;
    }
}

export function evaluateValidationRules(rules: ValidationRule[], value: string) {
    const statusById: Record<string, boolean> = {};
    const failedMessages: string[] = [];

    rules.forEach((rule) => {
        let met: boolean;
        try {
            met = rule.validate(value);
        } catch {
            met = false;
        }

        statusById[rule.id] = met;
        if (!met) {
            failedMessages.push(messageFor(rule, value));
        }
    });

    return {
        statusById,
        failedMessages,
        items: rules.map((rule) => ({
            id: rule.id,
            text: messageFor(rule, value),
            met: statusById[rule.id],
        })),
    };
}

export function getFailedValidationMessages(rules: ValidationRule[], value: string) {
    return evaluateValidationRules(rules, value).failedMessages;
}

export function getFirstFailedValidationMessage(rules: ValidationRule[], value: string) {
    return getFailedValidationMessages(rules, value)[0] ?? null;
}

export function collectValidationErrors(
    rulesMap: ValidationRulesMap,
    values: Record<string, string>,
    options: CollectValidationErrorsOptions = {}
): ValidationErrorsMap {
    const {stopAtFirstErrorPerField = false} = options;
    const validationErrors: ValidationErrorsMap = {};

    Object.entries(rulesMap).forEach(([fieldName, rules]) => {
        const failedMessages = getFailedValidationMessages(rules, values[fieldName] ?? "");
        if (failedMessages.length === 0) {
            return;
        }

        validationErrors[fieldName] = stopAtFirstErrorPerField
            ? [failedMessages[0]]
            : failedMessages;
    });

    return validationErrors;
}

export function createStringSchemaFromRules(rules: ValidationRule[], baseSchema: z.ZodString = z.string()) {
    return baseSchema.superRefine((value, ctx) => {
        const {failedMessages} = evaluateValidationRules(rules, value);
        failedMessages.forEach((message) => {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message,
            });
        });
    });
}

/**
 * The requirement messages, for suppressing a SERVER field error that repeats one of them.
 *
 * Deliberately reads the static `text` and not `textFor` (`CREATE-PUBLISH-36`): this set exists to
 * dedup messages the backend sends, and the backend only ever sends the static one — its own floor is
 * `RequestValidationConstants`, pinned against this file by `DiscussionPublishRequirementParityTests`.
 * A state-dependent variant is frontend-only and has no server twin to collide with.
 */
export function getRequirementMessageSet(rules: ValidationRule[]) {
    return new Set(rules.map((rule) => rule.text));
}

export function getFieldErrors(error?: HookFormFieldError) {
    if (!error) return [];

    const messages = new Set<string>();
    const addMessage = (value: unknown) => {
        if (typeof value === "string" && value.trim().length > 0) {
            messages.add(value);
        }
    };

    addMessage(error.message);

    if (error.types && typeof error.types === "object") {
        for (const value of Object.values(error.types)) {
            if (Array.isArray(value)) {
                value.forEach(addMessage);
            } else {
                addMessage(value);
            }
        }
    }

    return Array.from(messages).map((message) => ({message}));
}

export function getNonRequirementFieldErrors(
    error: HookFormFieldError | undefined,
    requirementMessages: Set<string>
) {
    return getFieldErrors(error).filter(
        (fieldError) => fieldError.message && !requirementMessages.has(fieldError.message)
    );
}

export function mergeRequirementItemsWithFieldErrors(
    items: RequirementItem[],
    error: HookFormFieldError | undefined,
    requirementMessages: Set<string>
) {
    const extraMessages = getNonRequirementFieldErrors(error, requirementMessages)
        .map((fieldError) => fieldError.message?.trim())
        .filter((message): message is string => !!message);

    if (extraMessages.length === 0) {
        return items;
    }

    const seen = new Set(items.map((item) => item.text.trim()));
    const merged = [...items];

    extraMessages.forEach((message, index) => {
        if (seen.has(message)) {
            return;
        }

        seen.add(message);
        merged.push({
            id: `server-validation-${index}-${message}`,
            text: message,
            met: false,
        });
    });

    return merged;
}

export function getNonRequirementMessages(
    messages: string[] | undefined,
    requirementMessages: Set<string>
) {
    if (!Array.isArray(messages)) {
        return [];
    }

    return [...new Set(messages.filter((message) => message && !requirementMessages.has(message)))];
}

/**
 * Content-equality helpers for this hook's four state records.
 *
 * <p>AGENTS.md rule 31: a merge helper that unconditionally constructs its result is the store defect
 * one layer down — it must return the object it was GIVEN when the content is identical, compared by
 * content, since a value rebuilt from an evaluation preserves no element identity.
 *
 * <p>`validateField` runs on every title and body keystroke in the composer. Before these guards it
 * minted four fresh records per call regardless of the answer, so a keystroke that changed no verdict
 * still scheduled four updates and pushed a brand-new `RequirementFieldState` (fresh `items` array)
 * through `getRequirementsState` into the editor. React 19 flushes passive effects inside a Sync
 * commit and counts them toward `nestedUpdateCount`, so that churn is not merely wasted work — it is
 * the budget React error #185 exhausts, six of which hit the composer in prod on 2026-09-03.
 * See designs/08-audits-and-process/2026-09-03-prod-multi-source-log-review-12h.md.
 */
function sameMessages(a: string[] | undefined, b: string[]): boolean {
    if (a === b) return true;
    if (a === undefined || a.length !== b.length) return false;
    return a.every((message, index) => message === b[index]);
}

function sameStatus(a: Record<string, boolean> | undefined, b: Record<string, boolean>): boolean {
    if (a === b) return true;
    if (a === undefined) return false;
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    return aKeys.every((key) => a[key] === b[key]);
}

/** Writes one field's entry, returning the record unchanged when the entry already says this. */
function withEntry<T>(
    previous: Record<string, T>,
    fieldName: string,
    next: T,
    isEqual: (current: T | undefined, candidate: T) => boolean,
): Record<string, T> {
    if (fieldName in previous && isEqual(previous[fieldName], next)) {
        return previous;
    }
    return {...previous, [fieldName]: next};
}

/** Same, for the multi-field writes in `validateAll`. */
function withEntries<T>(
    previous: Record<string, T>,
    next: Record<string, T>,
    isEqual: (current: T | undefined, candidate: T) => boolean,
): Record<string, T> {
    const changed = Object.keys(next).some((key) => !(key in previous) || !isEqual(previous[key], next[key]));
    return changed ? {...previous, ...next} : previous;
}

const sameBoolean = (a: boolean | undefined, b: boolean) => a === b;

export function useRequirements(
    getValidationRules: () => ValidationRulesMap,
    getFieldValue: (fieldName: string) => string,
    options: UseRequirementsOptions = {}
) {
    const {
        autoShowOnValidate = true,
        hideWhenSatisfied = true,
        showOnlyFailedOnValidateAll = false,
    } = options;
    const [reqVisible, setReqVisible] = React.useState<Record<string, boolean>>({});
    const [reqStatus, setReqStatus] = React.useState<Record<string, Record<string, boolean>>>({});
    const [reqHighlight, setReqHighlight] = React.useState<Record<string, boolean>>({});
    const [failedRequirements, setFailedRequirements] = React.useState<Record<string, string[]>>({});

    function validateField(fieldName: string, value: string, highlight: boolean = false): string[] {
        const rules = getValidationRules()[fieldName] ?? [];
        const evaluation = evaluateValidationRules(rules, value);
        const hasFailures = evaluation.failedMessages.length > 0;

        setReqStatus((prev) => withEntry(prev, fieldName, evaluation.statusById, sameStatus));
        setFailedRequirements((prev) => withEntry(prev, fieldName, evaluation.failedMessages, sameMessages));

        setReqHighlight((prev) => {
            const wasHighlighted = prev[fieldName] ?? false;
            const nextHighlight = highlight
                ? hasFailures
                : !autoShowOnValidate && wasHighlighted && hasFailures;

            return withEntry(prev, fieldName, nextHighlight, sameBoolean);
        });
        if (highlight) {
            setReqVisible((prev) => {
                const next = {...prev};
                Object.keys(getValidationRules()).forEach((key) => {
                    next[key] = true;
                });
                // `CREATE-PUBLISH-45`, 2026-09-02. The field this call actually EVALUATED does not take
                // the blanket `true` above: it takes the same `hideWhenSatisfied` answer every other
                // path in this hook gives, including `validateAll` immediately below, which has always
                // written `hasFailures` rather than `true`.
                //
                // Why. `requestPreview` validates the BODY with `highlight=true`, the body PASSES, and
                // execution falls through to the blank-title branch, which refuses the publish and
                // bounces the author back to the title field. The blanket `true` had meanwhile put the
                // requirements panel on screen carrying a single satisfied tick, so the one surface
                // that exists to answer "why didn't Publish work?" answered it with a green checkmark
                // for a requirement that was never the problem. Not a transition artifact either:
                // `reqVisible.sourceJson` clears only on the next BODY edit, and the author has just
                // been sent to the TITLE, so the all-met panel is the steady state.
                //
                // A satisfied gate may not present itself as the reason. A field that genuinely fails
                // is unchanged: `hasFailures` is true and the panel opens exactly as before.
                next[fieldName] = hideWhenSatisfied ? hasFailures : true;
                // The blanket write above makes a content comparison worth doing rather than assuming:
                // `highlight=true` fires on the Publish press rather than per keystroke, but it can
                // still be pressed twice with nothing changed in between.
                const unchanged = Object.keys(prev).length === Object.keys(next).length
                    && Object.keys(next).every((key) => key in prev && prev[key] === next[key]);
                return unchanged ? prev : next;
            });
        } else {
            setReqVisible((prev) => {
                const wasVisible = prev[fieldName] ?? false;
                const nextVisible = autoShowOnValidate
                    ? hideWhenSatisfied ? hasFailures : true
                    : wasVisible
                        ? hideWhenSatisfied ? hasFailures : true
                        : false;

                return withEntry(prev, fieldName, nextVisible, sameBoolean);
            });
        }

        return evaluation.failedMessages;
    }

    function validateAll(): Record<string, string[]> {
        const rulesMap = getValidationRules();
        const visible: Record<string, boolean> = {};
        const highlight: Record<string, boolean> = {};
        const nextStatus: Record<string, Record<string, boolean>> = {};
        const result: Record<string, string[]> = {};

        Object.keys(rulesMap).forEach((fieldName) => {
            const value = getFieldValue(fieldName) ?? "";
            const evaluation = evaluateValidationRules(rulesMap[fieldName] ?? [], value);
            const hasFailures = evaluation.failedMessages.length > 0;

            visible[fieldName] = showOnlyFailedOnValidateAll || hideWhenSatisfied ? hasFailures : true;
            highlight[fieldName] = showOnlyFailedOnValidateAll || hideWhenSatisfied ? hasFailures : true;
            nextStatus[fieldName] = evaluation.statusById;

            if (hasFailures) {
                result[fieldName] = evaluation.failedMessages;
            }
        });

        setReqVisible((prev) => withEntries(prev, visible, sameBoolean));
        setReqHighlight((prev) => withEntries(prev, highlight, sameBoolean));
        setReqStatus((prev) => withEntries(prev, nextStatus, sameStatus));
        // A full replacement rather than a merge (a field that now passes must DROP its messages), so
        // it is compared field-wise in both directions before being written.
        setFailedRequirements((prev) => {
            const prevKeys = Object.keys(prev);
            const unchanged = prevKeys.length === Object.keys(result).length
                && prevKeys.every((key) => sameMessages(prev[key], result[key] ?? []));
            return unchanged ? prev : result;
        });

        return result;
    }

    const getRequirementsState = React.useCallback((fieldName: string): RequirementFieldState => {
        const rules = getValidationRules()[fieldName] ?? [];
        const status = reqStatus[fieldName] ?? {};
        // `CREATE-PUBLISH-46`, 2026-09-02. `messageFor`, not `rule.text`. This function is the SOLE
        // producer of the composer's rendered requirement items, and it was reading the static string
        // while the only other producer, `evaluateValidationRules`, read the state-dependent one — so
        // a rule with a `textFor` had two messages and the screen always got the wrong one.
        //
        // Concretely, that is why `CREATE-PUBLISH-36`'s "Start writing your post." never reached a
        // phone: the body rule resolves that message at plain-text length 0 and the character-floor
        // message otherwise, and every captured frame of an EMPTY composer showed the character floor.
        // The value is read through `getFieldValue`, the same live-ref reader `validateAll` uses, so
        // the message tracks what the member currently has rather than a render-time snapshot.
        const value = getFieldValue(fieldName) ?? "";

        return {
            items: rules.map((rule) => ({
                id: rule.id,
                text: messageFor(rule, value),
                met: status[rule.id] ?? false,
            })),
            visible: reqVisible[fieldName] ?? false,
            highlight: reqHighlight[fieldName] ?? false,
            failedMessages: failedRequirements[fieldName] ?? [],
        };
    }, [failedRequirements, getFieldValue, getValidationRules, reqHighlight, reqStatus, reqVisible]);

    const renderRequirementList = (fieldName: string) => {
        const state = getRequirementsState(fieldName);
        return (
            <RequirementsList
                listId={`${fieldName}-reqs`}
                items={state.items}
                visible={state.visible}
                highlight={state.highlight}
                variant="plain"
            />
        );
    };

    const showRequirements = (fieldName: string) => {
        const rules = getValidationRules()[fieldName] ?? [];
        const value = getFieldValue(fieldName) ?? "";
        const evaluation = evaluateValidationRules(rules, value);

        setReqStatus((prev) => withEntry(prev, fieldName, evaluation.statusById, sameStatus));
        setFailedRequirements((prev) => withEntry(prev, fieldName, evaluation.failedMessages, sameMessages));
        setReqVisible((prev) => withEntry(
            prev,
            fieldName,
            hideWhenSatisfied ? evaluation.failedMessages.length > 0 : true,
            sameBoolean,
        ));
    };

    const hideRequirementsIfEmpty = (fieldName: string) => {
        const value = getFieldValue(fieldName) ?? "";
        if (!value) {
            setReqVisible((prev) => withEntry(prev, fieldName, false, sameBoolean));
        }
    };

    function resetRequirements() {
        setReqVisible({});
        setReqStatus({});
        setReqHighlight({});
        setFailedRequirements({});
    }

    return {
        reqVisible,
        reqStatus,
        reqHighlight,
        failedRequirements,
        validateField,
        validateAll,
        renderRequirementList,
        getRequirementsState,
        showRequirements,
        hideRequirementsIfEmpty,
        setFailedRequirements,
        setReqVisible,
        resetRequirements,
    } as const;
}

export default useRequirements;
